import React, { useContext } from 'react';
import { useRouter } from 'next/router';
import classnames from 'classnames/bind';
import Cookies from 'js-cookie';

import { DropdownItem, Icon, Menu, OnSelectProps } from 'gd-design-library';
import type { ChatMessengerElement } from '@/types/chat-messenger';
import { TENANT_STORAGE_KEY, TenantState } from '@constants/tenant';
import { TenantContext } from '@utils/TenantContext';
import { migrateToTenantStorage } from '@utils/googleShoppingAgentStorage';
import { ROUTES } from '@constants';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';
import styles from './TenantSelector.module.scss';

const tenantWhitelist: Set<string> | null = ((): Set<string> | null => {
  const raw = process.env.NEXT_PUBLIC_TENANT_WHITELIST;
  if (!raw) return null;

  return new Set(raw.split(',').map(k => k.trim()).filter(Boolean));
})();

const CN = 'tenant-selector';
const manageTenantsTextClassName = `${CN}--manage-tenants-text`;
const cx = classnames.bind(styles);

export const TenantSelector: React.FC = () => {
  const { push } = useRouter();
  const selectedTenant = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || 'null');
  const tenantContext = useContext(TenantContext);

  const handleTenantChange = ({ data }: OnSelectProps): void => {
    if (data.value === 'manage') {
      push(ROUTES.tenantManagement);

      return;
    }
    const newTenantId = data.value as string;
    Cookies.set(TENANT_STORAGE_KEY, JSON.stringify(tenantContext.tenants.find(tenant => tenant.id === newTenantId)), {
      expires: 1
    });
    if (selectedTenant?.key) {
      migrateToTenantStorage(selectedTenant.key);
    }
    (document.querySelector('chat-messenger') as ChatMessengerElement)?.startNewSession();
    window.location.href = '/';
  };

  if (!isFeatureEnabled(FEATURES.MULTI_TENANCY)) {
    return null;
  }

  return (
    <div className={ cx(CN) }>
      <Menu
        onSelect={ handleTenantChange }
        content={
          [ ...(tenantContext.tenants
            ?.filter(tenant => tenant.state === TenantState.READY)
            ?.filter(tenant => !tenantWhitelist || tenantWhitelist.has(tenant.key))
            ?.map(tenant => (
              <DropdownItem key={ tenant.id } value={ tenant.id } disabled={ tenant.id === selectedTenant.id }>
                {tenant.name}
              </DropdownItem>
            )) ?? []),
          ...(isFeatureEnabled(FEATURES.MANAGE_TENANTS) ? [ <DropdownItem key='manage' value='manage'>
            <Icon name='edit' />
            <span className={ cx(manageTenantsTextClassName) }>Manage</span>
          </DropdownItem> ] : []) ]
        }
      >
        {selectedTenant?.name || 'Select Tenant'}
        <Icon name='arrowDown' width={ 20 } height={ 20 } />
      </Menu>
    </div>
  );
};

